import React from "react";
import t from "../../../../i18n";
import { useProjects } from "../../../../api/useProjects";
import Switch from "@ui/Switch";
import { buildOptions, FilterWrapper, NONE_LABEL, useSubStateSetter } from "@ui/Filter";
import FilterSelect from "@ui/Filter/FilterSelect";
import createGlobalState from "@ui/lib/createGlobalState";
import { nullable } from "diagnostics-lib/io";
import * as io from "io-ts";
import Reset from "@ui/Filter/Reset";
import { styled } from "@linaria/react";
import SearchInputV2 from "@ui/Input/SearchInputV2";
import { useCategories } from "../../../../api/useCategories";
import { useTranslations } from "../../../../api/useTranslations";
import { reparabilities, Reparability } from "../../../../api/Reparability";

const SwitchWrapper = styled.div`
  display: flex;
  gap: inherit;
`;

const FilterValues = io.strict({
  project: io.array(io.string),
  projectType: io.array(nullable(io.string)),
  projectSite: io.array(nullable(io.string)),
  system: io.array(nullable(io.string)),
  reparability: io.array(nullable(Reparability)),
  degradationType: io.array(nullable(io.string)),
});

type FilterValues = io.TypeOf<typeof FilterValues>;
const DEFAULT_FILTER_VALUES: FilterValues = {
  project: [],
  projectType: [],
  projectSite: [],
  system: [],
  reparability: [],
  degradationType: [],
};
export const useFilterValues = createGlobalState({
  storage: "session",
  key: "adminFailuresTableFilterValues",
  type: FilterValues,
  defaultValue: DEFAULT_FILTER_VALUES,
  scoped: false,
});

interface P {
  onDisplayDraftChange: () => void;
  onDisplayManualFailuresChange: () => void;
  displayDraft: boolean;
  displayManualFailures: boolean;
  searchState: readonly [string, (n: string) => void];
  resetPagination: () => void;
  filterValues: FilterValues;
  setFilterValues: (v: React.SetStateAction<FilterValues>) => void;
}

const FailureFilters = ({
  displayDraft,
  displayManualFailures,
  onDisplayDraftChange,
  onDisplayManualFailuresChange,
  searchState,
  resetPagination,
  filterValues,
  setFilterValues,
}: P) => {
  const translations = useTranslations();
  const projects = useProjects();
  const [search, setSearch] = searchState;
  const systems = useCategories("SYSTEM");
  const degradationTypes = useCategories("DEGRADATION_TYPE");

  const includedProjects = React.useMemo(
    () => projects.filter(({ draft }) => !draft || (displayDraft && draft)),
    [displayDraft, projects],
  );

  const projectOptions = React.useMemo(
    () =>
      buildOptions(
        includedProjects.map(p => ({
          label: p.name,
          value: p.id.toString(),
        })),
      ),
    [includedProjects],
  );

  const projectTypeOptions = React.useMemo(() => {
    const allOptions = includedProjects.map(p => ({
      label: p.typeId ?? NONE_LABEL,
      value: p.typeId ?? null,
    }));
    return buildOptions(allOptions);
  }, [includedProjects]);

  const projectSiteOptions = React.useMemo(() => {
    const allOptions = includedProjects.map(p => ({
      label: p.siteId ?? NONE_LABEL,
      value: p.siteId ?? null,
    }));
    return buildOptions(allOptions);
  }, [includedProjects]);

  const systemOptions = React.useMemo(
    () =>
      buildOptions(
        [{ label: NONE_LABEL, value: null as string | null }].concat(
          systems.map(s => ({ label: s, value: s })),
        ),
      ),
    [systems],
  );

  const reparabilityOptions = React.useMemo(
    () =>
      buildOptions<Reparability | null>(
        [{ label: NONE_LABEL, value: null as Reparability | null }].concat(
          reparabilities.map(v => ({
            label: t("reparability", v, translations),
            value: v,
          })),
        ),
      ),
    [translations],
  );

  const degradationTypeOptions = React.useMemo(
    () =>
      buildOptions(
        [{ label: NONE_LABEL, value: null as string | null }].concat(
          degradationTypes.map(s => ({ label: s, value: s })),
        ),
      ),
    [degradationTypes],
  );

  const handleSearchChange = React.useCallback(
    (search: string) => {
      resetPagination();
      setSearch(search);
    },
    [resetPagination, setSearch],
  );

  const reparabilityFilterSelect = (
    <FilterSelect<Reparability | null>
      title={t("administration.failures.table.header.reparability")}
      options={reparabilityOptions}
      values={filterValues["reparability"]}
      setValues={useSubStateSetter(setFilterValues, "reparability", resetPagination)}
    />
  );

  return (
    <>
      <SearchInputV2
        data-cy="search-failures"
        placeholder={t("commons.placeholder.search")}
        onChange={e => handleSearchChange(e.currentTarget.value)}
        value={search}
      />
      <FilterWrapper>
        <FilterSelect<string>
          title={t("administration.quantities.table.header.project_name")}
          options={projectOptions}
          filtered
          values={filterValues["project"]}
          setValues={useSubStateSetter(setFilterValues, "project", resetPagination)}
        />
        <FilterSelect<string | null>
          title={t("administration.quantities.table.header.project_type", translations)}
          options={projectTypeOptions}
          filtered
          values={filterValues["projectType"]}
          setValues={useSubStateSetter(setFilterValues, "projectType", resetPagination)}
        />
        <FilterSelect<string | null>
          title={t("administration.quantities.table.header.project_site")}
          options={projectSiteOptions}
          filtered
          values={filterValues["projectSite"]}
          setValues={useSubStateSetter(setFilterValues, "projectSite", resetPagination)}
        />
        <FilterSelect<string | null>
          title={t("administration.quantities.table.header.system")}
          options={systemOptions}
          filtered
          values={filterValues["system"]}
          setValues={useSubStateSetter(setFilterValues, "system", resetPagination)}
        />
        {displayManualFailures ? null : reparabilityFilterSelect}
        <FilterSelect<string | null>
          title={t("administration.failures.table.header.degradation_type")}
          options={degradationTypeOptions}
          filtered
          values={filterValues["degradationType"]}
          setValues={useSubStateSetter(setFilterValues, "degradationType", resetPagination)}
        />
        <Reset
          filterValues={filterValues}
          setFilterValues={setFilterValues}
          defaultFilterValues={DEFAULT_FILTER_VALUES}
        />
      </FilterWrapper>
      <SwitchWrapper>
        <Switch checked={displayDraft} onChange={onDisplayDraftChange}>
          {t("projects.header.display_draft")}
        </Switch>
        <Switch checked={displayManualFailures} onChange={onDisplayManualFailuresChange}>
          {t("projects.header.display_manual_failures")}
        </Switch>
      </SwitchWrapper>
    </>
  );
};

export default React.memo(FailureFilters);
